import { Button, Grid, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import demoStockInputEntries from "config/demo/stockEntryInput.json";
import StockEntryInputForm from "components/agro/stockEntryInputs/StockEntryInputForm";

const StockInputEntries = () => {
  const [entries] = useState((demoStockInputEntries && demoStockInputEntries.slice) ? demoStockInputEntries.slice(0, 10) : []);
  const [entryDetails, setEntryDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  const [modalForm, setModalForm] = useState({
    visible: false,
    itemId: null,
    action: null,
  });

  const columnsMapped = useMemo(
    () =>
      entries.map((item) => ({
        ...item,
        actions: (
          <>
            <ActionIcon
              name={entryDetails?.id === item.id ? strings.entries.hide : strings.entries.show}
              icon={entryDetails?.id === item.id ? "VisibilityOff" : "Visibility"}
              color="primary"
              onClick={() => {
                entryDetails?.id === item.id ? setEntryDetails(null) : setEntryDetails(item);
              }}
            />
            <ActionIcon
              name={strings.entries.edit}
              icon="Edit"
              color="primary"
              onClick={() => openModal("update", item.id)}
            />
            <ActionIcon
              name={strings.entries.delete}
              icon="Delete"
              color="red.60"
              onClick={() =>
                setModalDetails({
                  visible: true,
                  itemId: item.id,
                  itemName: item.inputName || item.id,
                })
              }
            />
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, entryDetails?.id]
  );

  const openModal = (action, id = null) => setModalForm({ visible: true, action, itemId: id });

  const tableColumns = [
    { key: "date", label: strings.table.headers.date, _style: { width: "18%" } },
    { key: "warehouseId", label: strings.table.headers.warehouse, _style: { width: "20%" } },
    { key: "inputId", label: strings.table.headers.inputName, _style: { width: "22%" } },
    { key: "quantity", label: strings.table.headers.quantity, _style: { width: "12%" } },
    { key: "unit", label: strings.table.headers.unit, _style: { width: "10%" } },
    { key: "actions", label: "", _style: { width: "18%" } },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonAdd}
        children={strings.entries.addEntry}
        variation="primary"
        onClick={() => openModal("create")}
      />
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <SearchField label={strings.table.search.label} placeholder={strings.table.search.placeholder} />
        </Grid>

        <CTable
          className="table-centered"
          items={columnsMapped}
          columns={tableColumns}
          tableHeadProps={{ color: "light" }}
          responsive
          hover
        />

        <CCollapse visible={!!entryDetails?.date} style={styles.collapseCard}>
          <CCard key={entryDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{entryDetails?.inputName || entryDetails?.id}"
              </h4>

              <p className="text-muted"><b>{strings.details.date}:</b> {entryDetails?.date ? moment(entryDetails.date).format("YYYY-MM-DD") : "-"}</p>

              <p className="text-muted"><b>{strings.details.warehouse}:</b> {entryDetails?.warehouseName ?? entryDetails?.warehouseId ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.inputName}:</b> {entryDetails?.inputName ?? entryDetails?.inputId ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.activeIngredient}:</b> {entryDetails?.activeIngredient ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.unit}:</b> {entryDetails?.unit ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.classification}:</b> {entryDetails?.classification ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.physicalType}:</b> {entryDetails?.physicalType ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.withdrawalPeriodDays}:</b> {entryDetails?.withdrawalPeriodDays ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.reentryPeriodHours}:</b> {entryDetails?.reentryPeriodHours ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.icaRegistration}:</b> {entryDetails?.icaRegistration ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.toxicologicalCategory}:</b> {entryDetails?.toxicologicalCategory ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.quantity}:</b> {entryDetails?.quantity ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.performedBy}:</b> {entryDetails?.performedById ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.photoEvidence}:</b> {(Array.isArray(entryDetails?.photoEvidence) ? entryDetails.photoEvidence.join(", ") : entryDetails?.photoEvidence) ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.unitPrice}:</b> {entryDetails?.unitPrice ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.totalPrice}:</b> {entryDetails?.totalPrice ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.supplierName}:</b> {entryDetails?.supplierName ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.supplierTaxId}:</b> {entryDetails?.supplierTaxId ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.purchaseInvoice}:</b> {entryDetails?.purchaseInvoice ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.expirationDate}:</b> {entryDetails?.expirationDate ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.storageLocation}:</b> {entryDetails?.storageLocation ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.shelf}:</b> {entryDetails?.shelf ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.shelfLevel}:</b> {entryDetails?.shelfLevel ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.observations}:</b> {entryDetails?.observations ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.createdAt}:</b> {entryDetails?.createdAt ? moment(entryDetails.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}</p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, { name: modalDetails.itemName })}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
      />

      {modalForm.action && (
        <CModal size="xl" alignment="center" visible={modalForm.visible} onClose={() => setModalForm({ visible: false })} backdrop="static">
          <CModalHeader>
            <CModalTitle>{modalForm.action === "create" ? strings.modalForm.addTitle : strings.modalForm.updateTitle}</CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <StockEntryInputForm
                type={modalForm.action}
                initialValues={modalForm.action === "update" ? entries.find((e) => e.id === modalForm.itemId) || {} : {}}
                onSubmit={() => {}}
                onCancel={() => setModalForm({ visible: false })}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Insumos",
    entries: {
      addEntry: "Ingresar Insumo",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    table: {
      headers: {
        date: "Fecha",
        warehouse: "Almacén",
        inputName: "Insumo",
        quantity: "Cantidad",
        unit: "Unidad",
        receivedAt: "Fecha",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles Ingreso Insumo",
      date: "Fecha",
      warehouse: "Almacén",
      inputId: "Insumo",
      inputName: "Insumo",
      activeIngredient: "Ingrediente activo",
      unit: "Unidad",
      classification: "Clasificación",
      physicalType: "Tipo físico",
      withdrawalPeriodDays: "Periodo de carencia (días)",
      reentryPeriodHours: "Periodo de reentrada (horas)",
      icaRegistration: "Registro ICA",
      toxicologicalCategory: "Categoría toxicológica",
      quantity: "Cantidad",
      performedBy: "Persona que realiza",
      photoEvidence: "Evidencia fotográfica",
      unitPrice: "Valor unitario",
      totalPrice: "Valor total",
      supplierName: "Proveedor",
      supplierTaxId: "NIT / Identificación",
      purchaseInvoice: "Factura (PDF)",
      expirationDate: "Fecha de vencimiento",
      storageLocation: "Ubicación de almacenamiento",
      shelf: "Estante",
      shelfLevel: "Nivel del estante",
      observations: "Observaciones",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el ingreso {name}?",
    },
    modalForm: {
      addTitle: "Registrar Ingreso de Insumo",
      updateTitle: "Actualizar Ingreso de Insumo",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  collapseCard: { marginTop: "15px" },
};

export default StockInputEntries;
